import { useState, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { MetaTags } from "@/components/seo/MetaTags";
import { useNotificationSettings, useUpdateNotificationSettings } from "@/hooks/useNotificationSettings";
import { useProfile } from "@/hooks/useProfile";
import { useAuth } from "@/hooks/useAuth";
import { useWebhooks } from "@/hooks/useWebhooks";
import { api } from "@/lib/api-client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import {
  useIntegrationStatuses,
  useTestIntegration,
  useDisconnectIntegration,
} from "@/hooks/useIntegrations";
import {
  ArrowLeft,
  Search,
  ChevronDown,
  ChevronUp,
  CheckCircle2,
  XCircle,
  Loader2,
  Play,
  Plug,
  Zap,
  RefreshCw,
  ExternalLink,
  AlertTriangle,
  Github,
  Globe,
  Mail,
  MessageSquare,
  Webhook,
  Activity,
  Send,
  Bell,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { supabase } from "@/integrations/supabase/client";
import { WebhookConfig } from "@/components/settings/WebhookConfig";

// ────────────────────────────────────────────────────────────
// INLINE BRAND LOGOS — 32×32 SVG React Components
// ────────────────────────────────────────────────────────────

const SlackLogo = () => (
  <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
    <path d="M5.042 15.165a2.528 2.528 0 0 1-2.52 2.523A2.528 2.528 0 0 1 0 15.165a2.527 2.527 0 0 1 2.522-2.52h2.52v2.52zm1.27 0a2.527 2.527 0 0 1 2.521-2.52 2.527 2.527 0 0 1 2.521 2.52v6.313A2.528 2.528 0 0 1 8.833 24a2.528 2.528 0 0 1-2.521-2.522v-6.313zm2.521-10.123a2.528 2.528 0 0 1-2.521-2.52A2.528 2.528 0 0 1 8.833 0a2.528 2.528 0 0 1 2.521 2.522v2.52H8.833zm0 1.27a2.528 2.528 0 0 1 2.521 2.521 2.528 2.528 0 0 1-2.521 2.521H2.522A2.528 2.528 0 0 1 0 8.833a2.528 2.528 0 0 1 2.522-2.521h6.311zm10.122 2.521a2.528 2.528 0 0 1 2.522-2.521A2.528 2.528 0 0 1 24 8.833a2.528 2.528 0 0 1-2.522 2.521h-2.522V8.833zm-1.268 0a2.528 2.528 0 0 1-2.523 2.521 2.527 2.527 0 0 1-2.52-2.521V2.522A2.527 2.527 0 0 1 15.165 0a2.528 2.528 0 0 1 2.523 2.522v6.311zm-2.523 10.122a2.528 2.528 0 0 1 2.523 2.522A2.528 2.528 0 0 1 15.165 24a2.527 2.527 0 0 1-2.52-2.522v-2.522h2.52zm0-1.268a2.527 2.527 0 0 1-2.52-2.523 2.526 2.526 0 0 1 2.52-2.52h6.313A2.527 2.527 0 0 1 24 15.165a2.528 2.528 0 0 1-2.522 2.523h-6.313z" fill="#E01E5A"/>
  </svg>
);

const GithubLogo = () => (
  <svg width="28" height="28" viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 0C5.37 0 0 5.37 0 12c0 5.31 3.435 9.795 8.205 11.385.6.105.825-.255.825-.57 0-.285-.015-1.23-.015-2.235-3.015.555-3.795-.735-4.035-1.41-.135-.345-.72-1.41-1.23-1.695-.42-.225-1.02-.78-.015-.795.945-.015 1.62.87 1.845 1.23 1.08 1.815 2.805 1.305 3.495.99.105-.78.42-1.305.765-1.605-2.67-.3-5.46-1.335-5.46-5.925 0-1.305.465-2.385 1.23-3.225-.12-.3-.54-1.53.12-3.18 0 0 1.005-.315 3.3 1.23.96-.27 1.98-.405 3-.405s2.04.135 3 .405c2.295-1.56 3.3-1.23 3.3-1.23.66 1.65.24 2.88.12 3.18.765.84 1.23 1.905 1.23 3.225 0 4.605-2.805 5.625-5.475 5.925.435.375.81 1.095.81 2.22 0 1.605-.015 2.895-.015 3.3 0 .315.225.69.825.57A12.02 12.02 0 0 0 24 12c0-6.63-5.37-12-12-12z"/>
  </svg>
);

const VercelLogo = () => (
  <svg width="28" height="28" viewBox="0 0 24 24" fill="currentColor">
    <path d="M24 22.525H0l12-21.05 12 21.05z"/>
  </svg>
);

const TelegramLogo = () => (
  <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
    <circle cx="12" cy="12" r="12" fill="#2CA5E0"/>
    <path d="M5.491 11.74L18.51 6.68c.633-.23 1.186.154.98.983l-2.17 10.22c-.163.736-.6.916-1.215.57l-3.32-2.447-1.605 1.545c-.178.177-.327.327-.67.327l.237-3.382 6.138-5.545c.268-.237-.058-.37-.413-.133L7.93 14.82l-3.285-.996c-.714-.224-.728-.714.155-1.084z" fill="white"/>
  </svg>
);

const DiscordLogo = () => (
  <svg width="28" height="28" viewBox="0 0 24 24" fill="#5865F2">
    <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057c.003.025.015.05.032.063a19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028 14.09 14.09 0 0 0 1.226-1.994.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z"/>
  </svg>
);

const LinearLogo = () => (
  <svg width="28" height="28" viewBox="0 0 100 100" fill="none">
    <circle cx="50" cy="50" r="50" fill="#5E6AD2"/>
    <path d="M20.16 70.84l-0.58-0.58A35.01 35.01 0 0 1 14.8 44.67l40.53 40.53a35.01 35.01 0 0 1-25.59-4.78l-0.58-0.58v0zm-2.41-7.95L65.11 110.25A35 35 0 0 1 17.75 62.89zm53.97 14.73L14.4 20.44A35 35 0 1 1 71.72 77.62zM29.56 19.13L83.87 73.44A35 35 0 0 0 29.56 19.13z" fill="white" transform="scale(0.55) translate(18, 18)"/>
  </svg>
);

const JiraLogo = () => (
  <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
    <path d="M11.571 11.513H0a5.218 5.218 0 0 0 5.232 5.215h2.13v2.057A5.215 5.215 0 0 0 12.575 24V12.518a1.005 1.005 0 0 0-1.004-1.005z" fill="#2684FF"/>
    <path d="M6.308 6.308H0a5.218 5.218 0 0 0 5.234 5.215h2.13V9.466A5.215 5.215 0 0 0 12.577 14.76V3.264a1.005 1.005 0 0 0-1.005-1.005H6.308v4.05z" fill="url(#jira-a)"/>
    <path d="M12.572 1H1a5.218 5.218 0 0 0 5.234 5.215h2.13V4.157A5.215 5.215 0 0 0 13.576 9.45V-2a1.005 1.005 0 0 0-1.004-1v-0z" fill="url(#jira-b)" transform="translate(0,2)"/>
    <defs>
      <linearGradient id="jira-a" x1="12.577" y1="9.953" x2="6.308" y2="14.225" gradientUnits="userSpaceOnUse">
        <stop stopColor="#0052CC"/><stop offset="1" stopColor="#2684FF"/>
      </linearGradient>
      <linearGradient id="jira-b" x1="13.033" y1="4.782" x2="6.74" y2="9.392" gradientUnits="userSpaceOnUse">
        <stop stopColor="#0052CC"/><stop offset="1" stopColor="#2684FF"/>
      </linearGradient>
    </defs>
  </svg>
);

const DatadogLogo = () => (
  <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
    <path d="M21.19 6.31l-4.52-3.85L14.1 0l-1.96 1.56L11.49.91 9.54 0 8.11 1.12 7.17.37 4.52 2.52.81 5.88 0 8.73l1.16 1.6-.41 2.78.48 2.61 1.98 3.4L9.2 24l.31.01 2.29-2.29 1.51 1.63 2.62-1.39 2.15 1.14 1.78-2.29 2.74-1.5.73-3.77-.95-1.33.37-3.61zM9.73 17.95l-3.81-3.99-.42-.63.84-1.17-.79-1.35.73-2.31 3.45-1.52 4.82.4L16.08 11l-.5 2.82-2.94 2.89z" fill="#632CA6"/>
  </svg>
);

const GrafanaLogo = () => (
  <svg width="28" height="28" viewBox="0 0 24 24" fill="#F46800">
    <path d="M9.593 3.028c.064-.007.128-.014.193-.014a5.08 5.08 0 0 1 .742.054c.22.033.43.079.633.14a5.04 5.04 0 0 1 1.14.508c.17.105.332.22.483.345.15.123.29.256.42.397.13.14.25.287.358.44.108.154.206.313.292.478.087.164.161.333.224.505.063.172.113.347.15.525s.055.358.063.54v1.49H9.593V3.028zm4.814 0v5.43h3.481V3.028H14.407zM5.113 8.458H1.633V3.028h3.48v5.43zm4.48 0V3.028H5.113v5.43h4.48zm4.814 0v5.434h3.481V8.458H14.407zm-4.814 0H5.113v5.434h4.48V8.458zm-4.48 0H1.633v5.434h3.48V8.458zm9.294 5.434v5.43h3.481v-5.43H14.407zm-4.814 5.43v-5.43H5.113v5.43h4.48zm-4.48 0H1.633v-5.43h3.48v5.43zm9.294.07v3.58h3.481v-3.58H14.407zm-4.814 3.58v-3.58H5.113v3.58h4.48zm-4.48 0H1.633v-3.58h3.48v3.58z"/>
  </svg>
);

const PagerDutyLogo = () => (
  <svg width="28" height="28" viewBox="0 0 24 24" fill="#06AC38">
    <path d="M16.667 0H7.333A7.333 7.333 0 0 0 0 7.333v9.334A7.333 7.333 0 0 0 7.333 24h9.334A7.333 7.333 0 0 0 24 16.667V7.333A7.333 7.333 0 0 0 16.667 0zm-2.222 17.56H10.11V20H8.333V4h5.112c2.998 0 4.444 1.46 4.444 3.89 0 2.42-1.446 3.858-4.444 3.858H10.11v1.82h4.334v2.004h-.001zm-4.334-5.72h3.89c1.628 0 2.222-.67 2.222-1.95 0-1.28-.594-1.886-2.222-1.886H10.11V11.84z"/>
  </svg>
);

const TeamsLogo = () => (
  <svg width="28" height="28" viewBox="0 0 24 24" fill="#6264A7">
    <path d="M20.625 8.188a3 3 0 1 0 0-6 3 3 0 0 0 0 6zm0 1.5c-1.66 0-5.625.835-5.625 2.5V14.5h7.5v-2.25c0-.95-.795-2.5-1.875-2.5zM12 11.094a3.609 3.609 0 1 0 0-7.22 3.609 3.609 0 0 0 0 7.22zM3.375 9.188a3 3 0 1 0 0-6 3 3 0 0 0 0 6zm-3.375 3c0-1.665 3.965-2.5 5.625-2.5.29 0 .625.02.969.055C5.648 10.44 5.25 11.57 5.25 12.813V14.5H0v-2.312zM18.75 15.813H5.25V19.5c0 .828.672 1.5 1.5 1.5h10.5c.828 0 1.5-.672 1.5-1.5v-3.688z"/>
  </svg>
);

const SentryLogo = () => (
  <svg width="28" height="28" viewBox="0 0 24 24" fill="#362D59">
    <path d="M14.172 2.354a2.052 2.052 0 0 0-3.544 0L.628 20.646A2.052 2.052 0 0 0 2.4 23.5h6.4c0-6.76-4.2-12.68-4.2-12.68L8.7 3.6l9.9 18.2h2.4a1.8 1.8 0 0 0 1.56-2.706z"/>
  </svg>
);

const EmailLogo = () => (
  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="2" y="4" width="20" height="16" rx="2"/>
    <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/>
  </svg>
);

const WebhookLogo = () => (
  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M18 16.98h-5.99c-1.1 0-1.95.94-2.48 1.9A4 4 0 0 1 2 17c.01-.7.2-1.4.57-2"/>
    <path d="m6 17 3.13-5.78c.53-.97.1-2.18-.5-3.1a4 4 0 1 1 6.89-4.06"/>
    <path d="m12 6 3.13 5.73C15.66 12.7 16.9 13 18 13a4 4 0 0 1 0 8"/>
  </svg>
);

// ────────────────────────────────────────────────────────────
// CONNECTION STATUS BADGE
// ────────────────────────────────────────────────────────────

function StatusBadge({ status }: { status: string }) {
  if (status === "connected") {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-emerald-50 border border-emerald-300 text-emerald-700 text-[9px] font-black uppercase tracking-wider rounded-none">
        <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 animate-pulse" />
        Connected
      </span>
    );
  }
  if (status === "error") {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-red-50 border border-red-300 text-red-700 text-[9px] font-black uppercase tracking-wider rounded-none">
        <span className="h-1.5 w-1.5 rounded-full bg-red-500" />
        Error
      </span>
    );
  }
  if (status === "connecting") {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-50 border border-blue-300 text-blue-700 text-[9px] font-black uppercase tracking-wider rounded-none">
        <Loader2 className="h-2.5 w-2.5 animate-spin" />
        Connecting
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-zinc-100 border border-zinc-300 text-zinc-500 text-[9px] font-black uppercase tracking-wider rounded-none">
      <span className="h-1.5 w-1.5 rounded-full bg-zinc-400" />
      Not Connected
    </span>
  );
}

// ────────────────────────────────────────────────────────────
// INTEGRATION CARD COMPONENT
// ────────────────────────────────────────────────────────────

interface IntegrationCardProps {
  logo: React.ReactNode;
  name: string;
  description: string;
  status: string;
  metadata?: string;
  errorMessage?: string;
  onConnect: () => void;
  onTest: () => void;
  onDisconnect: () => void;
  isTesting: boolean;
  isConnecting?: boolean;
  extraContent?: React.ReactNode;  // for expanded config forms
  testLabel?: string;
  connectLabel?: string;
}

function IntegrationCard({
  logo,
  name,
  description,
  status,
  metadata,
  errorMessage,
  onConnect,
  onTest,
  onDisconnect,
  isTesting,
  isConnecting,
  extraContent,
  testLabel = "Send Test",
  connectLabel = "Connect",
}: IntegrationCardProps) {
  const [showConfig, setShowConfig] = useState(false);
  const displayStatus = isConnecting ? "connecting" : status;

  return (
    <motion.div
      layout
      className="border-2 border-black bg-background rounded-none p-4 space-y-3 hover:shadow-[3px_3px_0_0_#000] transition-all"
    >
      {/* Header row */}
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 flex items-center justify-center shrink-0 border-2 border-black/10 bg-zinc-50 rounded-none">
            {logo}
          </div>
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-sm font-black uppercase tracking-tight">{name}</span>
              <StatusBadge status={displayStatus} />
            </div>
            <p className="text-[10px] text-zinc-500 font-medium mt-0.5 leading-tight">{description}</p>
            {metadata && displayStatus === "connected" && (
              <p className="text-[9px] text-emerald-600 font-black uppercase tracking-wider mt-1">{metadata}</p>
            )}
            {errorMessage && displayStatus === "error" && (
              <p className="text-[9px] text-red-600 font-bold mt-1">⚠ {errorMessage}</p>
            )}
          </div>
        </div>
      </div>

      {/* Actions row */}
      <div className="flex items-center gap-2 flex-wrap pt-1 border-t border-black/8">
        {displayStatus === "not_connected" && (
          <>
            <Button
              onClick={extraContent ? () => setShowConfig(!showConfig) : onConnect}
              className="h-8 rounded-none border-2 border-black bg-black text-white hover:bg-white hover:text-black font-black uppercase text-[10px] px-3 gap-1.5"
            >
              <Plug className="h-3 w-3" />
              {connectLabel}
            </Button>
            {extraContent && (
              <span className="text-[9px] text-zinc-400 font-bold uppercase">
                {showConfig ? "▲ hide setup" : "▼ show setup"}
              </span>
            )}
          </>
        )}

        {displayStatus === "connected" && (
          <>
            <Button
              onClick={onTest}
              disabled={isTesting}
              variant="outline"
              className="h-8 rounded-none border-2 border-black bg-white hover:bg-zinc-50 font-black uppercase text-[10px] px-3 gap-1.5"
            >
              {isTesting ? <Loader2 className="h-3 w-3 animate-spin" /> : <Send className="h-3 w-3" />}
              {isTesting ? "Sending..." : testLabel}
            </Button>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button
                  variant="ghost"
                  className="h-8 rounded-none border-2 border-zinc-200 text-zinc-500 hover:text-red-600 hover:border-red-300 font-black uppercase text-[10px] px-3"
                >
                  Disconnect
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent className="rounded-none border-4 border-black bg-background shadow-[6px_6px_0_0_#000] p-6">
                <AlertDialogHeader>
                  <AlertDialogTitle className="text-base font-black uppercase">Disconnect {name}?</AlertDialogTitle>
                  <AlertDialogDescription className="text-xs font-bold text-muted-foreground uppercase mt-2">
                    This removes the connection. You can reconnect at any time.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter className="mt-4 gap-2">
                  <AlertDialogCancel className="rounded-none border-2 border-black bg-white font-black uppercase text-[10px] mt-0">
                    Cancel
                  </AlertDialogCancel>
                  <AlertDialogAction
                    onClick={onDisconnect}
                    className="rounded-none border-2 border-black bg-black text-white font-black uppercase text-[10px]"
                  >
                    Yes, Disconnect
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
            {extraContent && (
              <button
                onClick={() => setShowConfig(!showConfig)}
                className="h-8 px-2 text-[9px] font-black uppercase text-zinc-400 hover:text-black border-2 border-transparent hover:border-zinc-200"
              >
                {showConfig ? "▲ hide settings" : "▼ settings"}
              </button>
            )}
          </>
        )}

        {displayStatus === "error" && (
          <Button
            onClick={extraContent ? () => setShowConfig(!showConfig) : onConnect}
            className="h-8 rounded-none border-2 border-red-500 bg-red-50 text-red-700 hover:bg-red-500 hover:text-white font-black uppercase text-[10px] px-3 gap-1.5"
          >
            <RefreshCw className="h-3 w-3" />
            Reconnect
          </Button>
        )}

        {displayStatus === "connecting" && (
          <div className="flex items-center gap-2 text-[10px] font-black uppercase text-blue-600">
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
            Connecting...
          </div>
        )}
      </div>

      {/* Expanded config form (for token-based integrations) */}
      <AnimatePresence>
        {showConfig && extraContent && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="overflow-hidden"
          >
            <div className="pt-3 border-t-2 border-dashed border-black/20">
              {extraContent}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

// ────────────────────────────────────────────────────────────
// ACCORDION GROUP
// ────────────────────────────────────────────────────────────

function GroupSection({
  title,
  subtitle,
  defaultOpen,
  connectedCount,
  total,
  children,
}: {
  title: string;
  subtitle: string;
  defaultOpen: boolean;
  connectedCount: number;
  total: number;
  children: React.ReactNode;
}) {
  const [open, setOpen] = useState(defaultOpen);

  return (
    <div className="border-4 border-black rounded-none">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-5 py-4 bg-black text-white hover:bg-zinc-900 transition-colors"
      >
        <div className="flex items-center gap-3">
          <span className="text-sm font-black uppercase tracking-widest">{title}</span>
          <span className="text-[9px] font-bold text-zinc-400 uppercase">{subtitle}</span>
        </div>
        <div className="flex items-center gap-3">
          <span className={`text-[9px] font-black uppercase px-2 py-0.5 rounded-none ${connectedCount > 0 ? "bg-emerald-500 text-black" : "bg-zinc-700 text-zinc-400"}`}>
            {connectedCount}/{total} connected
          </span>
          {open ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
        </div>
      </button>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="overflow-hidden"
          >
            <div className="p-4 space-y-3 bg-zinc-50">
              {children}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// ────────────────────────────────────────────────────────────
// MAIN PAGE
// ────────────────────────────────────────────────────────────

export default function Integrations() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { data: settings, isLoading: settingsLoading } = useNotificationSettings();
  const updateSettings = useUpdateNotificationSettings();
  const { data: profile, refetch: refetchProfile } = useProfile();
  const { data: webhooks = [] } = useWebhooks();
  const { statuses, connectedCount, isLoading } = useIntegrationStatuses();
  const testMutation = useTestIntegration();
  const disconnectMutation = useDisconnectIntegration();

  const [search, setSearch] = useState("");
  const [testingKey, setTestingKey] = useState<string | null>(null);

  // Config dialog states (for token-based integrations)
  const [configDialog, setConfigDialog] = useState<string | null>(null);

  // Inline form values
  const [telegramToken, setTelegramToken] = useState("");
  const [telegramChatId, setTelegramChatId] = useState("");
  const [discordUrl, setDiscordUrl] = useState("");
  const [teamsUrl, setTeamsUrl] = useState("");
  const [emailTo, setEmailTo] = useState("");
  const [linearKey, setLinearKey] = useState("");
  const [jiraUrl, setJiraUrl] = useState("");
  const [jiraEmail, setJiraEmail] = useState("");
  const [jiraToken, setJiraToken] = useState("");
  const [datadogKey, setDatadogKey] = useState("");
  const [grafanaUrl, setGrafanaUrl] = useState("");
  const [grafanaKey, setGrafanaKey] = useState("");

  // Populate form values from existing settings
  useMemo(() => {
    if (settings) {
      setTelegramToken(settings.telegram_bot_token || "");
      setTelegramChatId(settings.telegram_chat_id || "");
      setDiscordUrl(settings.discord_webhook_url || "");
      setTeamsUrl(settings.teams_webhook_url || "");
      setEmailTo(settings.email_to_address || "");
      setLinearKey(settings.linear_api_key || "");
      setJiraUrl(settings.jira_url || "");
      setDatadogKey(settings.datadog_api_key || "");
      setGrafanaUrl(settings.grafana_url || "");
      setGrafanaKey(settings.grafana_api_key || "");
    }
  }, [settings]);

  const saveSettings = async (updates: any) => {
    await updateSettings.mutateAsync(updates);
  };

  const handleTest = async (key: string) => {
    setTestingKey(key);
    try {
      await testMutation.mutateAsync({ type: key, settings, profile });
    } finally {
      setTestingKey(null);
    }
  };

  const handleDisconnect = (key: string) => {
    disconnectMutation.mutate({
      type: key,
      onSave: saveSettings,
      onProfileUpdate: (updates) => api.updateProfile(updates).then(() => refetchProfile()),
    });
  };

  const handleGitHubConnect = () => {
    if (!user?.id) return;
    const clientId = import.meta.env.VITE_GITHUB_CLIENT_ID || "Iv23liyMeK3FE5a6lnEY";
    const redirectUri = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/github-oauth`;
    const authUrl = `https://github.com/login/oauth/authorize?client_id=${clientId}&redirect_uri=${encodeURIComponent(redirectUri)}&state=${user.id}&scope=repo,workflow&prompt=select_account`;
    window.location.href = authUrl;
  };

  const handleSlackConnect = async () => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) return toast.error("Please log in first");
    const workerUrl = import.meta.env.VITE_WORKER_URL || "https://uptimewing-worker.consenses.workers.dev";
    window.location.href = `${workerUrl}/api/slack/install?token=${session.access_token}`;
  };

  const handleTestAll = async () => {
    const connectedKeys = Object.entries(statuses)
      .filter(([, v]) => v.status === "connected")
      .map(([k]) => k);

    if (connectedKeys.length === 0) {
      toast.error("No integrations connected to test.");
      return;
    }

    toast.info(`Testing ${connectedKeys.length} integrations...`);
    for (const key of connectedKeys) {
      try {
        await testMutation.mutateAsync({ type: key, settings, profile });
      } catch { /* individual error already toasted */ }
    }
    toast.success("Test run complete.");
  };

  // Filter integrations by search
  const integrationMeta: Record<string, { name: string; keywords?: string }> = {
    slack: { name: "Slack", keywords: "alerts messages chat" },
    telegram: { name: "Telegram", keywords: "alerts bot mobile" },
    discord: { name: "Discord", keywords: "alerts webhook community" },
    email: { name: "Email Alerts", keywords: "smtp email notification" },
    teams: { name: "Microsoft Teams", keywords: "teams microsoft alerts" },
    github: { name: "GitHub", keywords: "code repo rollback" },
    vercel: { name: "Vercel Auto-Rollback", keywords: "deploy rollback vercel" },
    linear: { name: "Linear", keywords: "tickets issues tasks" },
    jira: { name: "Jira", keywords: "tickets issues jira" },
    datadog: { name: "Datadog", keywords: "metrics monitoring apm" },
    grafana: { name: "Grafana", keywords: "metrics dashboard graphs" },
    webhooks: { name: "Outgoing Webhooks", keywords: "api webhook custom" },
    pagerduty: { name: "PagerDuty", keywords: "on call incident pager" },
  };

  const matchesSearch = (key: string) => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    const meta = integrationMeta[key];
    return (
      meta?.name.toLowerCase().includes(q) ||
      meta?.keywords?.toLowerCase().includes(q) ||
      false
    );
  };

  if (isLoading || settingsLoading) {
    return (
      <div className="space-y-4 max-w-3xl mx-auto">
        {[1, 2, 3].map(i => (
          <div key={i} className="h-24 w-full bg-zinc-100 animate-pulse border-2 border-zinc-200" />
        ))}
      </div>
    );
  }

  const group1Keys = ["slack", "github", "vercel"];
  const group2Keys = ["telegram", "discord", "email", "teams", "pagerduty"];
  const group3Keys = ["linear", "jira"];
  const group4Keys = ["datadog", "grafana", "webhooks"];

  const countConnected = (keys: string[]) =>
    keys.filter(k => statuses[k]?.status === "connected").length;

  // Telegram config form
  const TelegramForm = (
    <div className="space-y-3">
      <p className="text-[10px] font-black uppercase text-zinc-500">
        Connect via our bot — message{" "}
        <a href={`https://t.me/${import.meta.env.VITE_TELEGRAM_BOT_USERNAME || "Uptimewingbot"}`} target="_blank" rel="noopener" className="text-blue-600 underline">
          @{import.meta.env.VITE_TELEGRAM_BOT_USERNAME || "Uptimewingbot"}
        </a>{" "}
        to get your Chat ID, or enter manually:
      </p>
      <div className="grid grid-cols-1 gap-2">
        <div>
          <label className="text-[9px] font-black uppercase text-zinc-500 block mb-1">Bot Token</label>
          <Input
            value={telegramToken}
            onChange={e => setTelegramToken(e.target.value)}
            placeholder="1234567890:AABBCC..."
            className="h-8 rounded-none border-2 border-black text-xs font-mono"
          />
        </div>
        <div>
          <label className="text-[9px] font-black uppercase text-zinc-500 block mb-1">Chat ID</label>
          <Input
            value={telegramChatId}
            onChange={e => setTelegramChatId(e.target.value)}
            placeholder="-100123456789"
            className="h-8 rounded-none border-2 border-black text-xs font-mono"
          />
        </div>
      </div>
      <div className="flex gap-2 pt-1">
        <Button
          onClick={async () => {
            if (!telegramToken || !telegramChatId) return toast.error("Enter both Bot Token and Chat ID");
            await saveSettings({ telegram_bot_token: telegramToken, telegram_chat_id: telegramChatId, telegram_enabled: true });
            toast.success("Telegram connected!");
          }}
          className="h-8 rounded-none border-2 border-black bg-black text-white font-black uppercase text-[10px] px-3"
        >
          Save & Connect
        </Button>
        <Button
          variant="outline"
          onClick={async () => {
            if (!telegramToken || !telegramChatId) return toast.error("Save credentials first");
            await handleTest("telegram");
          }}
          disabled={testingKey === "telegram"}
          className="h-8 rounded-none border-2 border-black font-black uppercase text-[10px] px-3"
        >
          {testingKey === "telegram" ? <Loader2 className="h-3 w-3 animate-spin" /> : "Test Message"}
        </Button>
      </div>
    </div>
  );

  // Discord config form
  const DiscordForm = (
    <div className="space-y-2">
      <label className="text-[9px] font-black uppercase text-zinc-500 block">Discord Webhook URL</label>
      <p className="text-[10px] text-zinc-400">Go to your Discord channel → Edit → Integrations → Webhooks → Copy URL</p>
      <div className="flex gap-2">
        <Input
          value={discordUrl}
          onChange={e => setDiscordUrl(e.target.value)}
          placeholder="https://discord.com/api/webhooks/..."
          className="h-8 rounded-none border-2 border-black text-xs font-mono flex-1"
        />
      </div>
      <div className="flex gap-2 pt-1">
        <Button
          onClick={async () => {
            if (!discordUrl) return toast.error("Enter webhook URL");
            await saveSettings({ discord_webhook_url: discordUrl, discord_enabled: true });
            toast.success("Discord connected!");
          }}
          className="h-8 rounded-none border-2 border-black bg-black text-white font-black uppercase text-[10px] px-3"
        >
          Save & Connect
        </Button>
        <Button
          variant="outline"
          onClick={() => handleTest("discord")}
          disabled={testingKey === "discord"}
          className="h-8 rounded-none border-2 border-black font-black uppercase text-[10px] px-3"
        >
          {testingKey === "discord" ? <Loader2 className="h-3 w-3 animate-spin" /> : "Test Ping"}
        </Button>
      </div>
    </div>
  );

  // Teams config form
  const TeamsForm = (
    <div className="space-y-2">
      <label className="text-[9px] font-black uppercase text-zinc-500 block">Teams Incoming Webhook URL</label>
      <p className="text-[10px] text-zinc-400">In Teams: Channel → … → Connectors → Incoming Webhook → Add → Copy URL</p>
      <div className="flex gap-2">
        <Input
          value={teamsUrl}
          onChange={e => setTeamsUrl(e.target.value)}
          placeholder="https://xxxxx.webhook.office.com/..."
          className="h-8 rounded-none border-2 border-black text-xs font-mono flex-1"
        />
      </div>
      <div className="flex gap-2 pt-1">
        <Button
          onClick={async () => {
            if (!teamsUrl) return toast.error("Enter webhook URL");
            await saveSettings({ teams_webhook_url: teamsUrl, teams_enabled: true });
            toast.success("Microsoft Teams connected!");
          }}
          className="h-8 rounded-none border-2 border-black bg-black text-white font-black uppercase text-[10px] px-3"
        >
          Save & Connect
        </Button>
        <Button
          variant="outline"
          onClick={() => handleTest("teams")}
          disabled={testingKey === "teams"}
          className="h-8 rounded-none border-2 border-black font-black uppercase text-[10px] px-3"
        >
          {testingKey === "teams" ? <Loader2 className="h-3 w-3 animate-spin" /> : "Test Message"}
        </Button>
      </div>
    </div>
  );

  // Linear config form
  const LinearForm = (
    <div className="space-y-2">
      <label className="text-[9px] font-black uppercase text-zinc-500 block">Linear API Key</label>
      <p className="text-[10px] text-zinc-400">
        Get it at:{" "}
        <a href="https://linear.app/settings/api" target="_blank" rel="noopener" className="text-blue-600 underline">linear.app/settings/api</a>
        {" "}→ Personal API Keys → Create key
      </p>
      <div className="flex gap-2">
        <Input
          value={linearKey}
          onChange={e => setLinearKey(e.target.value)}
          placeholder="lin_api_..."
          className="h-8 rounded-none border-2 border-black text-xs font-mono flex-1"
          type="password"
        />
      </div>
      <div className="flex gap-2 pt-1">
        <Button
          onClick={async () => {
            if (!linearKey) return toast.error("Enter your API key");
            await saveSettings({ linear_api_key: linearKey, linear_enabled: true });
            toast.success("Linear connected!");
          }}
          className="h-8 rounded-none border-2 border-black bg-black text-white font-black uppercase text-[10px] px-3"
        >
          Save & Connect
        </Button>
        <Button
          variant="outline"
          onClick={() => handleTest("linear")}
          disabled={testingKey === "linear"}
          className="h-8 rounded-none border-2 border-black font-black uppercase text-[10px] px-3"
        >
          {testingKey === "linear" ? <Loader2 className="h-3 w-3 animate-spin" /> : "Verify Key"}
        </Button>
      </div>
    </div>
  );

  // Jira config form
  const JiraForm = (
    <div className="space-y-2">
      <p className="text-[10px] text-zinc-400">
        Go to{" "}
        <a href="https://id.atlassian.com/manage-profile/security/api-tokens" target="_blank" rel="noopener" className="text-blue-600 underline">
          Atlassian API Tokens
        </a>{" "}
        → Create token. Your email is the username.
      </p>
      <div className="grid grid-cols-1 gap-2">
        <div>
          <label className="text-[9px] font-black uppercase text-zinc-500 block mb-1">Jira URL</label>
          <Input value={jiraUrl} onChange={e => setJiraUrl(e.target.value)} placeholder="https://yourorg.atlassian.net" className="h-8 rounded-none border-2 border-black text-xs font-mono" />
        </div>
        <div>
          <label className="text-[9px] font-black uppercase text-zinc-500 block mb-1">Email</label>
          <Input value={jiraEmail} onChange={e => setJiraEmail(e.target.value)} placeholder="you@company.com" className="h-8 rounded-none border-2 border-black text-xs font-mono" type="email" />
        </div>
        <div>
          <label className="text-[9px] font-black uppercase text-zinc-500 block mb-1">API Token</label>
          <Input value={jiraToken} onChange={e => setJiraToken(e.target.value)} placeholder="ATATT3xFfGF0..." className="h-8 rounded-none border-2 border-black text-xs font-mono" type="password" />
        </div>
      </div>
      <div className="flex gap-2 pt-1">
        <Button
          onClick={async () => {
            if (!jiraUrl || !jiraEmail || !jiraToken) return toast.error("Fill all Jira fields");
            await saveSettings({ jira_url: jiraUrl, jira_api_key: `${jiraEmail}:${jiraToken}`, jira_enabled: true });
            toast.success("Jira connected!");
          }}
          className="h-8 rounded-none border-2 border-black bg-black text-white font-black uppercase text-[10px] px-3"
        >
          Save & Connect
        </Button>
        <Button
          variant="outline"
          onClick={() => handleTest("jira")}
          disabled={testingKey === "jira"}
          className="h-8 rounded-none border-2 border-black font-black uppercase text-[10px] px-3"
        >
          {testingKey === "jira" ? <Loader2 className="h-3 w-3 animate-spin" /> : "Verify"}
        </Button>
      </div>
    </div>
  );

  // Datadog config form
  const DatadogForm = (
    <div className="space-y-2">
      <p className="text-[10px] text-zinc-400">
        Get your API key at{" "}
        <a href="https://app.datadoghq.com/organization-settings/api-keys" target="_blank" rel="noopener" className="text-blue-600 underline">Datadog → Org Settings → API Keys</a>
      </p>
      <Input
        value={datadogKey}
        onChange={e => setDatadogKey(e.target.value)}
        placeholder="Your Datadog API Key"
        className="h-8 rounded-none border-2 border-black text-xs font-mono"
        type="password"
      />
      <div className="flex gap-2 pt-1">
        <Button
          onClick={async () => {
            if (!datadogKey) return toast.error("Enter API key");
            await saveSettings({ datadog_api_key: datadogKey, datadog_enabled: true });
            toast.success("Datadog connected!");
          }}
          className="h-8 rounded-none border-2 border-black bg-black text-white font-black uppercase text-[10px] px-3"
        >
          Save & Connect
        </Button>
        <Button variant="outline" onClick={() => handleTest("datadog")} disabled={testingKey === "datadog"}
          className="h-8 rounded-none border-2 border-black font-black uppercase text-[10px] px-3">
          {testingKey === "datadog" ? <Loader2 className="h-3 w-3 animate-spin" /> : "Verify Key"}
        </Button>
      </div>
    </div>
  );

  // Grafana config form
  const GrafanaForm = (
    <div className="space-y-2">
      <p className="text-[10px] text-zinc-400">
        In Grafana: Configuration → API Keys → Add API Key → Role: Viewer → Create
      </p>
      <div className="grid grid-cols-1 gap-2">
        <div>
          <label className="text-[9px] font-black uppercase text-zinc-500 block mb-1">Grafana URL</label>
          <Input value={grafanaUrl} onChange={e => setGrafanaUrl(e.target.value)} placeholder="https://grafana.yourorg.com" className="h-8 rounded-none border-2 border-black text-xs font-mono" />
        </div>
        <div>
          <label className="text-[9px] font-black uppercase text-zinc-500 block mb-1">API Key</label>
          <Input value={grafanaKey} onChange={e => setGrafanaKey(e.target.value)} placeholder="eyJ..." className="h-8 rounded-none border-2 border-black text-xs font-mono" type="password" />
        </div>
      </div>
      <div className="flex gap-2 pt-1">
        <Button
          onClick={async () => {
            if (!grafanaUrl || !grafanaKey) return toast.error("Enter URL and API key");
            await saveSettings({ grafana_url: grafanaUrl, grafana_api_key: grafanaKey, grafana_enabled: true });
            toast.success("Grafana connected!");
          }}
          className="h-8 rounded-none border-2 border-black bg-black text-white font-black uppercase text-[10px] px-3"
        >
          Save & Connect
        </Button>
        <Button variant="outline" onClick={() => handleTest("grafana")} disabled={testingKey === "grafana"}
          className="h-8 rounded-none border-2 border-black font-black uppercase text-[10px] px-3">
          {testingKey === "grafana" ? <Loader2 className="h-3 w-3 animate-spin" /> : "Verify"}
        </Button>
      </div>
    </div>
  );

  return (
    <div className="max-w-3xl mx-auto space-y-6 pb-12">
      <MetaTags
        title="Connect Integrations — UptimeWing"
        description="Connect Slack, GitHub, Telegram, Discord, Vercel and more. Real 1-click connections — no manual API key hunting."
        canonical="https://uptimewing.com/integrations"
      />

      {/* ─── HEADER ─── */}
      <div className="flex items-center justify-between border-b-2 border-black pb-5">
        <div className="flex items-center gap-3">
          <Link
            to="/dashboard"
            className="flex items-center justify-center h-8 w-8 border-2 border-black hover:bg-black hover:text-white transition-all"
          >
            <ArrowLeft className="h-4 w-4" />
          </Link>
          <div>
            <h1 className="text-2xl font-black uppercase tracking-tight leading-none">Integrations</h1>
            <p className="text-[10px] text-zinc-500 font-bold uppercase tracking-wider mt-0.5">
              {connectedCount} of 13 connected · 15 seconds to connect
            </p>
          </div>
        </div>
        <Button
          onClick={handleTestAll}
          disabled={connectedCount === 0}
          className="h-9 rounded-none border-2 border-black bg-black text-white hover:bg-[#FFFF00] hover:text-black font-black uppercase text-[10px] px-4 gap-2"
        >
          <Zap className="h-3.5 w-3.5" />
          Test All ({connectedCount})
        </Button>
      </div>

      {/* ─── SEARCH ─── */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-400" />
        <input
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Search integrations... (Slack, GitHub, Telegram...)"
          className="w-full h-10 pl-9 pr-4 border-2 border-black rounded-none text-sm font-medium focus:outline-none focus:ring-2 focus:ring-black bg-background"
        />
        {search && (
          <button
            onClick={() => setSearch("")}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-black"
          >
            <XCircle className="h-4 w-4" />
          </button>
        )}
      </div>

      {/* ─── GROUPS ─── */}
      <div className="space-y-4">
        {/* GROUP 1: QUICK START */}
        {group1Keys.some(matchesSearch) && (
          <GroupSection
            title="⚡ Quick Start"
            subtitle="Most popular — connect in 15 seconds"
            defaultOpen={true}
            connectedCount={countConnected(group1Keys)}
            total={group1Keys.filter(matchesSearch).length}
          >
            {/* SLACK */}
            {matchesSearch("slack") && (
              <IntegrationCard
                logo={<SlackLogo />}
                name="Slack"
                description="Get instant alerts in your Slack channels when a site goes down or recovers."
                status={statuses.slack?.status || "not_connected"}
                metadata={statuses.slack?.metadata}
                onConnect={handleSlackConnect}
                onTest={() => handleTest("slack")}
                onDisconnect={() => handleDisconnect("slack")}
                isTesting={testingKey === "slack"}
                connectLabel="Connect with Slack"
                testLabel="Send Test Alert"
              />
            )}

            {/* GITHUB */}
            {matchesSearch("github") && (
              <IntegrationCard
                logo={<GithubLogo />}
                name="GitHub"
                description="Automatically run workflows or create issues when your site goes down."
                status={statuses.github?.status || "not_connected"}
                metadata={statuses.github?.metadata}
                onConnect={handleGitHubConnect}
                onTest={() => handleTest("github")}
                onDisconnect={() => handleDisconnect("github")}
                isTesting={testingKey === "github"}
                connectLabel="Connect with GitHub"
                testLabel="Verify Connection"
              />
            )}

            {/* VERCEL AUTO-ROLLBACK */}
            {matchesSearch("vercel") && (
              <IntegrationCard
                logo={<VercelLogo />}
                name="Vercel Auto-Rollback"
                description="Automatically roll back to your last working Vercel deploy when downtime is detected."
                status={statuses.vercel?.status || "not_connected"}
                metadata={statuses.vercel?.metadata}
                onConnect={() => setConfigDialog("vercel")}
                onTest={() => handleTest("vercel")}
                onDisconnect={() => handleDisconnect("vercel")}
                isTesting={testingKey === "vercel"}
                connectLabel="Set Up Auto-Rollback"
              />
            )}
          </GroupSection>
        )}

        {/* GROUP 2: ALERTS */}
        {group2Keys.some(matchesSearch) && (
          <GroupSection
            title="🔔 Alert Channels"
            subtitle="Where you want to be notified"
            defaultOpen={countConnected(group2Keys) > 0}
            connectedCount={countConnected(group2Keys)}
            total={group2Keys.filter(matchesSearch).length}
          >
            {/* TELEGRAM */}
            {matchesSearch("telegram") && (
              <IntegrationCard
                logo={<TelegramLogo />}
                name="Telegram"
                description="Receive instant alerts on your phone via Telegram — works even when email is slow."
                status={statuses.telegram?.status || "not_connected"}
                metadata={statuses.telegram?.metadata}
                onConnect={() => {}}
                onTest={() => handleTest("telegram")}
                onDisconnect={() => handleDisconnect("telegram")}
                isTesting={testingKey === "telegram"}
                extraContent={TelegramForm}
                connectLabel="Set Up Telegram"
              />
            )}

            {/* DISCORD */}
            {matchesSearch("discord") && (
              <IntegrationCard
                logo={<DiscordLogo />}
                name="Discord"
                description="Post downtime alerts to your Discord server channel with one webhook URL."
                status={statuses.discord?.status || "not_connected"}
                metadata={statuses.discord?.metadata}
                onConnect={() => {}}
                onTest={() => handleTest("discord")}
                onDisconnect={() => handleDisconnect("discord")}
                isTesting={testingKey === "discord"}
                extraContent={DiscordForm}
                connectLabel="Set Up Discord"
              />
            )}

            {/* EMAIL */}
            {matchesSearch("email") && (
              <IntegrationCard
                logo={<EmailLogo />}
                name="Email Alerts"
                description="Get downtime and recovery emails delivered directly to your inbox."
                status={statuses.email?.status || "not_connected"}
                metadata={statuses.email?.metadata}
                onConnect={() => navigate("/settings?tab=notifications")}
                onTest={() => toast.info("Test email will be sent on next downtime event")}
                onDisconnect={() => handleDisconnect("email")}
                isTesting={false}
                connectLabel="Set Up Email"
                testLabel="Email Settings"
              />
            )}

            {/* MICROSOFT TEAMS */}
            {matchesSearch("teams") && (
              <IntegrationCard
                logo={<TeamsLogo />}
                name="Microsoft Teams"
                description="Send formatted downtime alerts directly to your Teams channel."
                status={statuses.teams?.status || "not_connected"}
                metadata={statuses.teams?.metadata}
                onConnect={() => {}}
                onTest={() => handleTest("teams")}
                onDisconnect={() => handleDisconnect("teams")}
                isTesting={testingKey === "teams"}
                extraContent={TeamsForm}
                connectLabel="Set Up Teams"
              />
            )}

            {/* PAGERDUTY */}
            {matchesSearch("pagerduty") && (
              <IntegrationCard
                logo={<PagerDutyLogo />}
                name="PagerDuty"
                description="Trigger on-call incidents in PagerDuty automatically when your site goes down."
                status={statuses.pagerduty?.status || "not_connected"}
                metadata={statuses.pagerduty?.metadata}
                onConnect={() => {
                  toast.info("PagerDuty OAuth is coming soon. Use outgoing webhooks to connect manually for now.");
                }}
                onTest={() => toast.info("PagerDuty OAuth coming soon")}
                onDisconnect={() => {}}
                isTesting={false}
                connectLabel="Coming Soon"
              />
            )}
          </GroupSection>
        )}

        {/* GROUP 3: DEVOPS */}
        {group3Keys.some(matchesSearch) && (
          <GroupSection
            title="🛠 Ticket & Project Tracking"
            subtitle="Auto-create tickets when things break"
            defaultOpen={countConnected(group3Keys) > 0}
            connectedCount={countConnected(group3Keys)}
            total={group3Keys.filter(matchesSearch).length}
          >
            {/* LINEAR */}
            {matchesSearch("linear") && (
              <IntegrationCard
                logo={<LinearLogo />}
                name="Linear"
                description="Automatically open a Linear issue when a monitor goes down, so your team can track the fix."
                status={statuses.linear?.status || "not_connected"}
                metadata={statuses.linear?.metadata}
                onConnect={() => {}}
                onTest={() => handleTest("linear")}
                onDisconnect={() => handleDisconnect("linear")}
                isTesting={testingKey === "linear"}
                extraContent={LinearForm}
                connectLabel="Connect Linear"
              />
            )}

            {/* JIRA */}
            {matchesSearch("jira") && (
              <IntegrationCard
                logo={<JiraLogo />}
                name="Jira"
                description="Create Jira tickets automatically when downtime is detected — close them on recovery."
                status={statuses.jira?.status || "not_connected"}
                metadata={statuses.jira?.metadata}
                onConnect={() => {}}
                onTest={() => handleTest("jira")}
                onDisconnect={() => handleDisconnect("jira")}
                isTesting={testingKey === "jira"}
                extraContent={JiraForm}
                connectLabel="Connect Jira"
              />
            )}
          </GroupSection>
        )}

        {/* GROUP 4: ADVANCED */}
        {group4Keys.some(matchesSearch) && (
          <GroupSection
            title="📊 Metrics & Advanced"
            subtitle="For engineering teams that want full observability"
            defaultOpen={countConnected(group4Keys) > 0}
            connectedCount={countConnected(group4Keys)}
            total={group4Keys.filter(matchesSearch).length}
          >
            {/* DATADOG */}
            {matchesSearch("datadog") && (
              <IntegrationCard
                logo={<DatadogLogo />}
                name="Datadog"
                description="Correlate UptimeWing incidents with your Datadog APM and logs for root-cause analysis."
                status={statuses.datadog?.status || "not_connected"}
                metadata={statuses.datadog?.metadata}
                onConnect={() => {}}
                onTest={() => handleTest("datadog")}
                onDisconnect={() => handleDisconnect("datadog")}
                isTesting={testingKey === "datadog"}
                extraContent={DatadogForm}
                connectLabel="Connect Datadog"
              />
            )}

            {/* GRAFANA */}
            {matchesSearch("grafana") && (
              <IntegrationCard
                logo={<GrafanaLogo />}
                name="Grafana"
                description="Push downtime events to your Grafana dashboard as annotations on your metrics graphs."
                status={statuses.grafana?.status || "not_connected"}
                metadata={statuses.grafana?.metadata}
                onConnect={() => {}}
                onTest={() => handleTest("grafana")}
                onDisconnect={() => handleDisconnect("grafana")}
                isTesting={testingKey === "grafana"}
                extraContent={GrafanaForm}
                connectLabel="Connect Grafana"
              />
            )}

            {/* OUTGOING WEBHOOKS */}
            {matchesSearch("webhooks") && (
              <div className="border-2 border-black bg-background p-4 space-y-3">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 flex items-center justify-center border-2 border-black/10 bg-zinc-50">
                    <WebhookLogo />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-black uppercase tracking-tight">Outgoing Webhooks</span>
                      <StatusBadge status={webhooks.length > 0 ? "connected" : "not_connected"} />
                    </div>
                    <p className="text-[10px] text-zinc-500 mt-0.5">
                      Send raw event payloads to any URL — integrate with Zapier, Make, n8n, or your own server.
                    </p>
                    {webhooks.length > 0 && (
                      <p className="text-[9px] text-emerald-600 font-black uppercase mt-1">
                        {webhooks.length} webhook{webhooks.length > 1 ? "s" : ""} active
                      </p>
                    )}
                  </div>
                </div>
                <div className="pt-2 border-t-2 border-dashed border-black/20">
                  <WebhookConfig />
                </div>
              </div>
            )}
          </GroupSection>
        )}

        {/* No search results */}
        {search && !["slack","telegram","discord","email","teams","github","vercel","linear","jira","datadog","grafana","webhooks","pagerduty"].some(matchesSearch) && (
          <div className="text-center py-12 text-zinc-500 font-mono text-sm uppercase font-bold border-2 border-dashed border-zinc-300">
            No integrations match "{search}"
          </div>
        )}
      </div>

      {/* ─── VERCEL SETUP DIALOG ─── */}
      <Dialog open={configDialog === "vercel"} onOpenChange={(o) => !o && setConfigDialog(null)}>
        <DialogContent className="rounded-none border-4 border-black bg-background shadow-[8px_8px_0_0_#000] p-6 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-base font-black uppercase flex items-center gap-2">
              <VercelLogo /> Set Up Vercel Auto-Rollback
            </DialogTitle>
            <DialogDescription className="text-xs text-zinc-500 uppercase font-bold mt-1">
              When your site goes down, UptimeWing will automatically trigger a rollback to your last working Vercel deployment.
            </DialogDescription>
          </DialogHeader>
          <div className="mt-4 space-y-3">
            <p className="text-[10px] text-zinc-400 bg-zinc-50 border-2 border-zinc-200 p-3 font-mono leading-relaxed">
              1. Go to Vercel Dashboard → Settings → Webhooks<br />
              2. Create a webhook for "deployment" events<br />
              3. Point it to your monitoring endpoint below<br />
              4. Copy the Vercel Deploy Hook URL and paste it here
            </p>
            <div>
              <label className="text-[9px] font-black uppercase text-zinc-500 block mb-1">Vercel Deploy Hook URL</label>
              <Input
                onChange={e => setTeamsUrl(e.target.value)} // reusing state for simplicity
                placeholder="https://api.vercel.com/v1/integrations/deploy/..."
                className="h-8 rounded-none border-2 border-black text-xs font-mono"
              />
            </div>
            <div className="flex gap-2 pt-2">
              <Button
                onClick={async () => {
                  if (!teamsUrl) return toast.error("Enter the Vercel deploy hook URL");
                  await saveSettings({ vercel_webhook_url: teamsUrl, vercel_enabled: true });
                  setConfigDialog(null);
                  toast.success("Vercel Auto-Rollback enabled!");
                }}
                className="h-8 rounded-none border-2 border-black bg-black text-white font-black uppercase text-[10px] px-3"
              >
                Enable Auto-Rollback
              </Button>
              <Button
                variant="ghost"
                onClick={() => setConfigDialog(null)}
                className="h-8 rounded-none border-2 border-zinc-200 font-black uppercase text-[10px] px-3"
              >
                Cancel
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
